import {
  NOT_IN_TAXONOMY,
  NOT_IN_TAXONOMY_NAME,
  REFERRAL,
  REFERRAL_NAME,
  UNCLASSIFIED,
  UNCLASSIFIED_NAME,
  type ClassificationScope,
  type ClassificationTreeNode,
  type PolicyViolationQueueSummary,
  type QueueClaimFilters
} from '@/api/moderationQueue';

// What narrows the pool. null is "all". A classification only means something inside one vertical; it
// covers its node's subtree unless the scope is "own" (the node's own level only).
export type QueueFilters = {
  vertical: string | null;
  classification: string | null;
  scope: 'own' | null;
  language: string | null;
  group: string | null;
};

export const NO_FILTERS: QueueFilters = {
  vertical: null,
  classification: null,
  scope: null,
  language: null,
  group: null
};

// The filters live in the URL query string, so a link to a filtered queue works.
export function readFilters(parameters: URLSearchParams): QueueFilters {
  const group = parameters.get('group') || null;
  const vertical = group ? null : parameters.get('vertical') || null;
  const classification = vertical ? parameters.get('classification') || null : null;
  return {
    vertical,
    classification,
    scope: classification && parameters.get('scope') === 'own' ? 'own' : null,
    language: parameters.get('language') || null,
    group
  };
}

// Writes the filters over a copy of the other parameters.
export function writeFilters(parameters: URLSearchParams, filters: QueueFilters): URLSearchParams {
  const next = new URLSearchParams(parameters);
  for (const [key, value] of Object.entries(filters)) {
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
  }

  return next;
}

export const hasFilters = (filters: QueueFilters) => Object.values(filters).some(Boolean);

// What a claim sends besides the vertical.
export function claimFilters(filters: QueueFilters): QueueClaimFilters {
  return {
    ...(filters.classification
      ? {
          classification: filters.classification,
          classificationScope: (filters.scope ?? 'subtree') satisfies ClassificationScope
        }
      : {}),
    ...(filters.language ? {language: filters.language} : {})
  };
}

// The filter value a tree node stands for: the three top-level buckets have fixed keys, a node is its concept id
// (a referring application's is `referral:<source>`). A node with no concept (one an admin made by hand) falls
// back to its name, which no task carries.
export function nodeKey(node: ClassificationTreeNode, topLevel: boolean): string {
  if (topLevel && node.name === UNCLASSIFIED_NAME) {
    return UNCLASSIFIED;
  }

  if (topLevel && node.name === REFERRAL_NAME) {
    return REFERRAL;
  }

  return topLevel && node.name === NOT_IN_TAXONOMY_NAME
    ? NOT_IN_TAXONOMY
    : (node.concept_id ?? node.name);
}

// The nodes from the top of the tree down to the node a classification filter names; null when none does.
export function classificationPath(
  tree: readonly ClassificationTreeNode[],
  classification: string
): ClassificationTreeNode[] | null {
  const search = (
    nodes: readonly ClassificationTreeNode[],
    trail: ClassificationTreeNode[]
  ): ClassificationTreeNode[] | null => {
    for (const node of nodes) {
      const path = [...trail, node];
      const key = nodeKey(node, trail.length === 0);
      if (key === classification) {
        return path;
      }

      const deeper = search(node.children, path);
      if (deeper) {
        return deeper;
      }
    }

    return null;
  };

  return search(tree, []);
}

// What a classification filter reads as: the vertical, then the nodes down to the selection.
export function classificationLabel(
  filters: QueueFilters,
  tree: readonly ClassificationTreeNode[]
) {
  const path = filters.classification ? classificationPath(tree, filters.classification) : null;
  return [
    ...(filters.vertical ? [filters.vertical] : []),
    ...(path?.map((node) => node.name) ?? (filters.classification ? [filters.classification] : []))
  ];
}

const selectionOf = (tree: readonly ClassificationTreeNode[], filters: QueueFilters) =>
  filters.classification
    ? (classificationPath(tree, filters.classification)?.at(-1) ?? null)
    : null;

// The work behind the vertical and classification filters (language and kind are already applied by
// the server that produced the summaries).
export function selectionCounts(
  summaries: readonly PolicyViolationQueueSummary[],
  filters: QueueFilters
): number {
  const rows = filters.vertical
    ? summaries.filter((summary) => summary.policy_violation === filters.vertical)
    : summaries;
  let total = 0;
  for (const row of rows) {
    const node = filters.classification ? selectionOf(row.tree, filters) : null;
    if (!filters.classification) {
      total += row.available;
    } else if (node) {
      total += filters.scope === 'own' ? node.own : node.available;
    }
  }

  return total;
}

// The tree pruned to the nodes whose name contains the query and the ancestors of such nodes; `open` holds
// the paths of the nodes that have a match beneath them, which a search keeps expanded.
export function searchTree(
  tree: readonly ClassificationTreeNode[],
  query: string
): {nodes: ClassificationTreeNode[]; open: Set<string>} {
  const needle = query.trim().toLowerCase();
  const open = new Set<string>();
  if (!needle) {
    return {nodes: [...tree], open};
  }

  const prune = (
    nodes: readonly ClassificationTreeNode[],
    parent: string
  ): ClassificationTreeNode[] =>
    nodes.flatMap((node) => {
      const path = `${parent}/${node.name}`;
      const children = prune(node.children, path);
      if (children.length > 0) {
        open.add(path);
      }

      return children.length > 0 || node.name.toLowerCase().includes(needle)
        ? [{...node, children}]
        : [];
    });
  return {nodes: prune(tree, ''), open};
}

let displayNames: Intl.DisplayNames | undefined;

// "en" -> "English"; null is an item that declares no language.
export function languageLabel(code: string | null): string {
  if (code === null) {
    return 'Unknown';
  }

  try {
    displayNames ??= new Intl.DisplayNames(['en'], {type: 'language'});
    return displayNames.of(code) ?? code;
  } catch {
    return code;
  }
}
